import {
  deriveFieldVisibility,
  isFieldVisible,
  pruneHiddenFields,
  type FieldVisibilityMap,
  type InitiativeSnapshot,
} from '@complan/domain';
import type { InitiativeWriteInput } from '@complan/contracts';

/**
 * The pure model behind the Initiative_Intake_Form (task 23.1).
 *
 * ## One visibility decision, two consumers (R5.2, R5.3, Property 23)
 *
 * The single load-bearing rule of this module is that the *rendered* field set
 * and the *validated* field set are computed from the **same** object: the domain
 * {@link FieldVisibilityMap} produced by {@link deriveFieldVisibility}. The form
 * never re-derives "is this section shown" from the involvement booleans on its
 * own — it asks {@link isFieldVisible} with the same map the Validation_Engine
 * consumes on the server. So a field the form hides can never be validated, and a
 * field validated can never be hidden; the two cannot drift because there is only
 * one source.
 *
 * The web layer therefore imports the visibility logic; it does not reimplement
 * it. {@link visibilityFromForm} converts the three involvement booleans the form
 * holds into a throw-away {@link InitiativeSnapshot} shell whose only meaningful
 * fields are those flags, hands it to {@link deriveFieldVisibility}, and returns
 * the resulting map. Everything downstream — which steps show conditional blocks,
 * which field paths are in play, what a discard prunes — reads that map.
 *
 * ## Logical field paths vs. the wire shape
 *
 * The domain map is keyed by *logical* field paths (`fieldForceScope.roles`,
 * `dte.dependencyDescription`, `csc.dueDate`, …) — the same paths the validation
 * registry reports a finding against. The contract write shape
 * ({@link InitiativeWriteInput}) instead carries `fieldForceScope[]` and a single
 * `dependencies[]` array discriminated by `dependencyFunction`. This module owns
 * the mapping between the two so no component has to: {@link SECTION_FIELD_PATHS}
 * declares which logical paths belong to each conditional section, and a section
 * is shown iff *any* of its paths is visible.
 */

/** The three conditional sections whose visibility the domain map governs. */
export type ConditionalSection = 'fieldForce' | 'dte' | 'csc';

/**
 * The logical field paths of each conditional section, exactly as the domain
 * {@link FieldVisibilityMap} keys them. Kept in this module (not re-derived) so a
 * change to the domain paths surfaces here as a type/test failure rather than a
 * silent divergence between what the form shows and what the server validates.
 */
export const SECTION_FIELD_PATHS: Readonly<Record<ConditionalSection, readonly string[]>> = {
  fieldForce: [
    'fieldForceScope.roles',
    'fieldForceScope.activityTypes',
    'fieldForceScope.audiences',
    'fieldForceScope.hasMaterial',
    'fieldForceScope.workOrder',
    'fieldForceScope.informationContribution',
  ],
  dte: [
    'dte.dependencyFunction',
    'dte.dependencyDescription',
    'dte.ownerUserId',
    'dte.dueDate',
  ],
  csc: [
    'csc.attributeIds',
    'csc.dependencyFunction',
    'csc.dependencyDescription',
    'csc.ownerUserId',
    'csc.dueDate',
  ],
} as const;

/** The involvement booleans the form tracks; the sole input to visibility. */
export interface InvolvementFlags {
  readonly fieldForceInvolvement: boolean;
  readonly dteInvolvement: boolean;
  readonly cscInvolvement: boolean;
}

/**
 * A minimal snapshot shell carrying only the three involvement flags.
 *
 * {@link deriveFieldVisibility} reads exactly
 * `snapshot.initiative.{fieldForceInvolvement,dteInvolvement,cscInvolvement}` and
 * nothing else, so the rest of the aggregate is filled with inert, type-correct
 * placeholders. Building a real snapshot here would duplicate the server's load;
 * the point is only to reach the one pure function that both layers call.
 */
function visibilitySnapshotShell(flags: InvolvementFlags): InitiativeSnapshot {
  const initiative = {
    id: '',
    initiativeRef: '',
    title: '',
    details: null,
    ownerUserId: '',
    backupOwnerUserId: null,
    startDate: '' as InitiativeSnapshot['initiative']['startDate'],
    endDate: '' as InitiativeSnapshot['initiative']['endDate'],
    platforms: [],
    channelIds: [],
    customerGroupIds: [],
    audienceIds: [],
    categoryIds: [],
    initiativeTypeIds: [],
    salesCycleIds: [],
    salesCycleSource: 'derived' as const,
    salesCycleOverrideReason: null,
    status: 'draft' as const,
    priorityId: '',
    isConfidential: false,
    authorizedViewerUserIds: [],
    fieldForceInvolvement: flags.fieldForceInvolvement,
    dteInvolvement: flags.dteInvolvement,
    cscInvolvement: flags.cscInvolvement,
    milestones: [],
    dependencies: [],
    fieldForceScope: [],
    version: 0,
  } satisfies InitiativeSnapshot['initiative'];

  return {
    initiative,
    masterData: {
      platforms: [],
      channels: [],
      customerGroups: [],
      audiences: [],
      categories: [],
      initiativeTypes: [],
      priorities: [],
      milestoneTypes: [],
      fieldForceActivityTypes: [],
    },
    salesCycles: [],
    accounts: [],
  };
}

/**
 * The {@link FieldVisibilityMap} for the current involvement flags — the single
 * object both the renderer and the validation engine consume.
 *
 * Delegates to the domain's {@link deriveFieldVisibility}; the web layer contains
 * no visibility rule of its own (task 23.1's binding constraint).
 */
export function visibilityFromForm(flags: InvolvementFlags): FieldVisibilityMap {
  return deriveFieldVisibility(visibilitySnapshotShell(flags));
}

/**
 * Whether a conditional section should be rendered.
 *
 * A section is visible iff at least one of its logical field paths is visible
 * under the map. Because the map maps every FF path to `fieldForceInvolvement`
 * (and likewise DTE/CSC), "any path visible" collapses to "the flag is yes" — but
 * expressing it through {@link isFieldVisible} is what keeps the renderer honest:
 * it is reading the same predicate the validator reads, not a parallel boolean.
 */
export function isSectionVisible(
  section: ConditionalSection,
  visibility: FieldVisibilityMap,
): boolean {
  return SECTION_FIELD_PATHS[section].some((path) => isFieldVisible(path, visibility));
}

/**
 * The set of logical field paths currently visible, across all conditional
 * sections. This is the *validated field set* the server evaluates and, by
 * construction, the same set the form renders (Property 23). Returned sorted so
 * two equal sets compare equal as arrays in a test.
 */
export function visibleFieldPaths(visibility: FieldVisibilityMap): readonly string[] {
  const paths: string[] = [];
  for (const section of Object.keys(SECTION_FIELD_PATHS) as ConditionalSection[]) {
    for (const path of SECTION_FIELD_PATHS[section]) {
      if (isFieldVisible(path, visibility)) {
        paths.push(path);
      }
    }
  }
  return paths.sort();
}

/**
 * The conditional sections currently rendered, for the involvement flags. This is
 * the *rendered field set* expressed at section granularity; a test asserts it is
 * exactly the set of sections all of whose paths appear in
 * {@link visibleFieldPaths}, i.e. rendered == validated.
 */
export function renderedSections(flags: InvolvementFlags): readonly ConditionalSection[] {
  const visibility = visibilityFromForm(flags);
  return (Object.keys(SECTION_FIELD_PATHS) as ConditionalSection[]).filter((section) =>
    isSectionVisible(section, visibility),
  );
}

/**
 * The affected fields a yes→no involvement toggle would discard, named for the
 * confirmation dialog (R5.10).
 *
 * A section's fields are "affected" only when the section currently holds a value
 * — an empty FF scope or an empty dependency list has nothing to discard, so
 * toggling it off needs no confirmation. The caller passes the current form
 * values; this returns the logical paths that would be pruned, which the dialog
 * resolves to labels through the catalogue.
 */
export function affectedFieldPathsOnToggleOff(
  section: ConditionalSection,
  form: HeldValuesView,
): readonly string[] {
  if (section === 'fieldForce') {
    return (form.fieldForceScope?.length ?? 0) > 0 ? [...SECTION_FIELD_PATHS.fieldForce] : [];
  }
  const holdsValue = (form.dependencies ?? []).some((dep) => dep?.dependencyFunction === section);
  return holdsValue ? [...SECTION_FIELD_PATHS[section]] : [];
}

/**
 * The minimal, loose view the toggle helpers read: the two conditional
 * collections, each possibly a partial/`undefined`-laced shape as RHF's
 * `useWatch` hands them back. Kept structural so a deep-partial watch value flows
 * in without a cast at the call site.
 */
export interface HeldValuesView {
  readonly fieldForceScope?: readonly unknown[] | undefined;
  readonly dependencies?: readonly ({ readonly dependencyFunction?: unknown } | undefined)[] | undefined;
}

/**
 * Whether toggling a section from yes to no requires a discard confirmation
 * (R5.10): only when it currently holds at least one value.
 */
export function toggleRequiresConfirmation(
  section: ConditionalSection,
  form: HeldValuesView,
): boolean {
  return affectedFieldPathsOnToggleOff(section, form).length > 0;
}

/**
 * Apply the submit-time prune (R5.10): drop the conditional values of any section
 * whose involvement flag is `no`.
 *
 * This is deliberately applied *at submit*, not on toggle, so cancelling a
 * discard confirmation is a genuine no-op — the values were never removed from
 * form state, only excluded here on the way out. It reuses the domain's
 * {@link pruneHiddenFields} so the pruning rule has one definition shared with
 * the server, then re-materialises the write payload with the pruned collections.
 */
export function pruneForSubmit(form: InitiativeWriteInput): InitiativeWriteInput {
  const flags: InvolvementFlags = {
    fieldForceInvolvement: form.fieldForceInvolvement ?? false,
    dteInvolvement: form.dteInvolvement ?? false,
    cscInvolvement: form.cscInvolvement ?? false,
  };

  // pruneHiddenFields reasons over the snapshot's fieldForceScope/dependencies,
  // so build a shell whose those two collections mirror the form, letting the
  // domain decide what to drop.
  const snapshot = visibilitySnapshotShell(flags);
  const withCollections: InitiativeSnapshot = {
    ...snapshot,
    initiative: {
      ...snapshot.initiative,
      fieldForceScope: (form.fieldForceScope ?? []).map((entry, index) => ({
        id: String(index),
        fieldForceRole: entry.fieldForceRole,
        activityTypeId: entry.activityTypeId,
        audienceId: entry.audienceId ?? null,
        hasMaterial: entry.hasMaterial ?? false,
        workOrder: entry.workOrder ?? null,
        informationContribution: entry.informationContribution ?? null,
      })),
      dependencies: (form.dependencies ?? []).map((dep, index) => ({
        id: String(index),
        dependencyFunction: dep.dependencyFunction,
        description: dep.description ?? null,
        ownerText: dep.ownerText ?? null,
        // The wire `dueDate` is a validated `YYYY-MM-DD` string; the snapshot
        // type brands it as PlainDate. pruneHiddenFields never inspects the date,
        // so the brand is a type-only concern here.
        dueDate: (dep.dueDate ?? null) as InitiativeSnapshot['initiative']['dependencies'][number]['dueDate'],
        dueEndDate: (dep.dueEndDate ?? null) as InitiativeSnapshot['initiative']['dependencies'][number]['dueEndDate'],
        completedAt: dep.completedAt ?? null,
      })),
    },
  };

  const pruned = pruneHiddenFields(withCollections);

  const next: InitiativeWriteInput = { ...form };
  if (pruned.fieldForceScope !== undefined) {
    // A pruned FF scope is always empty (the domain sets it to []).
    next.fieldForceScope = [];
  }
  if (!flags.cscInvolvement && (form.cscAttributeIds ?? []).length > 0) {
    // The CSC attributes live in the CSC block, hidden with it (R5.10), the same
    // rule the domain's pruneHiddenFields applies to them.
    next.cscAttributeIds = [];
  }
  if (pruned.dependencies !== undefined) {
    // The domain returns the surviving dependency rows; map them back to the
    // wire input shape by dependencyFunction.
    const survivingFunctions = new Set(pruned.dependencies.map((dep) => dep.dependencyFunction));
    next.dependencies = (form.dependencies ?? []).filter((dep) =>
      survivingFunctions.has(dep.dependencyFunction),
    );
  }
  return next;
}
